import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './context/AuthContext';
import { SiteConfigProvider } from './context/SiteConfigContext';
import { Layout } from './components/Layout';
import './App.css';

// Pages
import HomePage from './pages/HomePage';
import { DossiersListPage, DossierDetailPage } from './pages/NoticiasPage';
import { EditionsListPage, EditionDetailPage, EditionArticlePage } from './pages/EditionPage';
import { InfluenceIndexPage, InfluenceEntryPage } from './pages/InfluenceIndexPage';
import { EvidenceLibraryPage, EvidenceDetailPage } from './pages/EvidencePage';
import { LoginPage, RegisterPage, AuthCallbackPage } from './pages/AuthPages';
import { SubscriptionPage, SubscriptionSuccessPage } from './pages/SubscriptionPage';
import ProfilePage from './pages/ProfilePage';
import SearchPage from './pages/SearchPage';
import AdminPage from './pages/AdminPage';
import StaffManagementPage from './pages/StaffManagementPage';
import EditorWritePage from './pages/EditorWritePage';
import DesignerGalleryPage from './pages/DesignerGalleryPage';
import StaticPage from './pages/StaticPage';
import { BlogListPage, BlogDetailPage } from './pages/BlogPage';
import MyContentPage from './pages/MyContentPage';
import BlogEditorPage from './pages/BlogEditorPage';

// Router wrapper to detect session_id in hash
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id (Google Auth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallbackPage />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Layout><HomePage /></Layout>} />
      
      {/* Dossiers / Noticias */}
      <Route path="/dossiers" element={<Layout><DossiersListPage /></Layout>} />
      <Route path="/noticias" element={<Layout><DossiersListPage /></Layout>} />
      <Route path="/dossiers/:dossierId" element={<Layout><DossierDetailPage /></Layout>} />
      <Route path="/noticias/:dossierId" element={<Layout><DossierDetailPage /></Layout>} />
      
      {/* Blog */}
      <Route path="/blog" element={<Layout><BlogListPage /></Layout>} />
      <Route path="/blog/:blogId" element={<Layout><BlogDetailPage /></Layout>} />
      
      {/* Weekly Edition */}
      <Route path="/edition" element={<Layout><EditionsListPage /></Layout>} />
      <Route path="/edition/:editionId" element={<Layout><EditionDetailPage /></Layout>} />
      <Route path="/edition/:editionId/article/:articleIndex" element={<Layout><EditionArticlePage /></Layout>} />
      
      {/* Influence Index */}
      <Route path="/influence-index" element={<Layout><InfluenceIndexPage /></Layout>} />
      <Route path="/influence-index/:entryId" element={<Layout><InfluenceEntryPage /></Layout>} />
      
      {/* Evidence Library */}
      <Route path="/evidence" element={<Layout><EvidenceLibraryPage /></Layout>} />
      <Route path="/evidence/:evidenceId" element={<Layout><EvidenceDetailPage /></Layout>} />
      
      {/* Auth - Without Layout (has own full-screen design) */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      
      {/* User */}
      <Route path="/profile" element={<Layout><ProfilePage /></Layout>} />
      <Route path="/subscription" element={<Layout><SubscriptionPage /></Layout>} />
      <Route path="/subscription/success" element={<Layout><SubscriptionSuccessPage /></Layout>} />
      
      {/* Search */}
      <Route path="/search" element={<Layout><SearchPage /></Layout>} />
      
      {/* Static Pages */}
      <Route path="/page/:slug" element={<Layout><StaticPage /></Layout>} />
      
      {/* Admin */}
      <Route path="/admin" element={<Layout><AdminPage /></Layout>} />
      <Route path="/admin/staff" element={<Layout><StaffManagementPage /></Layout>} />
      <Route path="/admin/write" element={<EditorWritePage />} />
      <Route path="/admin/gallery" element={<DesignerGalleryPage />} />
      
      {/* Editor Routes */}
      <Route path="/editor/my-content" element={<MyContentPage />} />
      <Route path="/editor/blog/new" element={<BlogEditorPage />} />
      <Route path="/editor/blog/:blogId" element={<BlogEditorPage />} />
      
      {/* 404 */}
      <Route path="*" element={
        <Layout>
          <div className="container-editorial py-20 text-center">
            <h1 className="font-serif text-4xl font-bold text-navy mb-4">404</h1>
            <p className="text-gray-600 mb-8">Página no encontrada</p>
            <a href="/" className="text-navy font-medium hover:text-red">
              Volver al inicio
            </a>
          </div>
        </Layout>
      } />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SiteConfigProvider>
          <AppRouter />
          <Toaster position="bottom-right" />
        </SiteConfigProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
